<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import TimezoneSelect from '@/components/organizer/events/TimezoneSelect.vue'
import { FAVORITE_COUNTRIES, ALL_COUNTRIES } from '@/types/countries'
import type { VenueUsage } from '@/types/events'
import {
  browserTimezone,
  deriveCreateTimezone,
  deriveEditTimezone,
  type VenueTimezoneSource,
} from '@/utils/venueTimezone'

export interface VenueFormData {
  name: string | null
  street: string | null
  city: string | null
  postal_code: string | null
  country: string | null
  /** '' = no time zone chosen yet. */
  timezone: string
}

type TimezoneOrigin = VenueTimezoneSource | 'manual' | 'stored' | null

interface TimezoneSuggestion {
  timezone: string
  source: VenueTimezoneSource
}

const props = defineProps<{
  modelValue: VenueFormData
  errors: Record<string, string[]>
  mode: 'create' | 'edit'
  /** Unique per instance: RaceForm mounts two VenueSelects. */
  idPrefix: string
  /** Edit mode: the zone the venue was loaded with (drives the change warning). */
  initialTimezone?: string
  /** Edit mode: how many editions and races use the venue; null/absent = unknown. */
  usage?: VenueUsage | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: VenueFormData]
}>()

const { t } = useI18n()

const timezoneOrigin = ref<TimezoneOrigin>(props.mode === 'edit' ? 'stored' : null)
const suggestion = ref<TimezoneSuggestion | null>(null)

const visibleSuggestion = computed<TimezoneSuggestion | null>(() =>
  suggestion.value !== null && suggestion.value.timezone !== props.modelValue.timezone
    ? suggestion.value
    : null,
)

const hasCountry = computed<boolean>(() => props.modelValue.country !== null && props.modelValue.country !== '')

const timezoneSourceLine = computed<string | null>(() => {
  if (props.modelValue.timezone === '') {
    return hasCountry.value ? t('venues.timezone.manual_required') : null
  }
  switch (timezoneOrigin.value) {
    case 'venue_country':
      return t('venues.timezone.source_venue')
    case 'browser':
      return t('venues.timezone.source_browser')
    default:
      return null
  }
})

const timezoneChanged = computed<boolean>(
  () =>
    props.mode === 'edit' &&
    props.initialTimezone !== undefined &&
    props.modelValue.timezone !== '' &&
    props.modelValue.timezone !== props.initialTimezone,
)

const changeWarning = computed<string | null>(() => {
  if (!timezoneChanged.value) return null
  const usage = props.usage ?? null
  if (usage === null) return t('venues.timezone.change_warning_generic')
  if (usage.edition_count === 0 && usage.race_count === 0) return null
  return t('venues.timezone.change_warning', { editions: usage.edition_count, races: usage.race_count })
})

onMounted(() => {
  if (props.mode === 'create' && props.modelValue.timezone === '') {
    const result = deriveCreateTimezone({
      country: props.modelValue.country,
      browserTimezone: browserTimezone(),
    })
    if (result.timezone !== null) {
      timezoneOrigin.value = result.source
      emit('update:modelValue', { ...props.modelValue, timezone: result.timezone })
    }
  }
})

function update<K extends keyof VenueFormData>(field: K, value: VenueFormData[K]): void {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
}

function setTimezone(timezone: string, origin: TimezoneOrigin): void {
  timezoneOrigin.value = origin
  suggestion.value = null
  update('timezone', timezone)
}

function handleTimezoneChange(timezone: string): void {
  setTimezone(timezone, 'manual')
}

function applySuggestion(): void {
  if (suggestion.value === null) return
  setTimezone(suggestion.value.timezone, suggestion.value.source)
}

function handleCountryChange(event: Event): void {
  const raw = (event.target as HTMLSelectElement).value
  const country = raw === '' ? null : raw
  // Country and time zone are emitted in one update so neither overwrites the other.
  const base: VenueFormData = { ...props.modelValue, country }
  let timezone = props.modelValue.timezone
  const browser = browserTimezone()

  if (props.mode === 'create') {
    const result = deriveCreateTimezone({ country, browserTimezone: browser })
    if (timezoneOrigin.value === 'manual') {
      // A manual pick sticks; a different derivation is only suggested.
      suggestion.value =
        result.timezone !== null && result.source !== null && result.timezone !== timezone
          ? { timezone: result.timezone, source: result.source }
          : null
    } else {
      timezone = result.timezone ?? ''
      timezoneOrigin.value = result.source
      suggestion.value = null
    }
  } else {
    const derived = deriveEditTimezone({ country, browserTimezone: browser })
    if (derived.timezone === null || derived.source === null || derived.timezone === timezone) {
      suggestion.value = null
    } else if (props.usage?.race_count === 0 && timezoneOrigin.value !== 'manual') {
      // Nothing resolves to this venue yet: switching is safe (the change warning still shows).
      timezone = derived.timezone
      timezoneOrigin.value = derived.source
      suggestion.value = null
    } else {
      // Usage unknown, races exist, or the organiser picked the zone by hand: never switch, only suggest.
      suggestion.value = { timezone: derived.timezone, source: derived.source }
    }
  }

  emit('update:modelValue', { ...base, timezone })
}

const inputClass =
  'w-full rounded-md bg-slate-900 border px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-colors'
</script>

<template>
  <div class="space-y-4">
    <!-- Venue name -->
    <div>
      <label :for="`${idPrefix}-name`" class="block text-xs font-medium text-slate-400 mb-1">
        {{ t('venues.fields.name') }}
      </label>
      <input
        :id="`${idPrefix}-name`"
        :value="modelValue.name ?? ''"
        type="text"
        :class="[inputClass, errors['name'] ? 'border-red-500' : 'border-slate-600 focus:border-emerald-500']"
        @input="update('name', ($event.target as HTMLInputElement).value)"
      />
      <p v-if="errors['name']" class="mt-1 text-xs text-red-400">{{ errors['name'][0] }}</p>
    </div>

    <!-- Street -->
    <div>
      <label :for="`${idPrefix}-street`" class="block text-xs font-medium text-slate-400 mb-1">
        {{ t('venues.fields.street') }}
      </label>
      <input
        :id="`${idPrefix}-street`"
        :value="modelValue.street ?? ''"
        type="text"
        :class="[inputClass, errors['street'] ? 'border-red-500' : 'border-slate-600 focus:border-emerald-500']"
        @input="update('street', ($event.target as HTMLInputElement).value)"
      />
      <p v-if="errors['street']" class="mt-1 text-xs text-red-400">{{ errors['street'][0] }}</p>
    </div>

    <!-- City + Postal code -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label :for="`${idPrefix}-city`" class="block text-xs font-medium text-slate-400 mb-1">
          {{ t('venues.fields.city') }}
        </label>
        <input
          :id="`${idPrefix}-city`"
          :value="modelValue.city ?? ''"
          type="text"
          :class="[inputClass, errors['city'] ? 'border-red-500' : 'border-slate-600 focus:border-emerald-500']"
          @input="update('city', ($event.target as HTMLInputElement).value)"
        />
        <p v-if="errors['city']" class="mt-1 text-xs text-red-400">{{ errors['city'][0] }}</p>
      </div>
      <div>
        <label :for="`${idPrefix}-postal-code`" class="block text-xs font-medium text-slate-400 mb-1">
          {{ t('venues.fields.postal_code') }}
        </label>
        <input
          :id="`${idPrefix}-postal-code`"
          :value="modelValue.postal_code ?? ''"
          type="text"
          :class="[inputClass, errors['postal_code'] ? 'border-red-500' : 'border-slate-600 focus:border-emerald-500']"
          @input="update('postal_code', ($event.target as HTMLInputElement).value)"
        />
        <p v-if="errors['postal_code']" class="mt-1 text-xs text-red-400">{{ errors['postal_code'][0] }}</p>
      </div>
    </div>

    <!-- Country -->
    <div>
      <label :for="`${idPrefix}-country`" class="block text-xs font-medium text-slate-400 mb-1">
        {{ t('venues.fields.country') }}
      </label>
      <select
        :id="`${idPrefix}-country`"
        :value="modelValue.country ?? ''"
        class="w-full rounded-md bg-slate-900 border px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-colors"
        :class="errors['country'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500'"
        @change="handleCountryChange"
      >
        <option value="">{{ t('common.select_placeholder') }}</option>
        <optgroup label="-">
          <option
            v-for="country in FAVORITE_COUNTRIES"
            :key="'fav-' + country.code"
            :value="country.code"
          >
            {{ country.name }}
          </option>
        </optgroup>
        <optgroup label="-">
          <option
            v-for="country in ALL_COUNTRIES"
            :key="country.code"
            :value="country.code"
          >
            {{ country.name }}
          </option>
        </optgroup>
      </select>
      <p v-if="errors['country']" class="mt-1 text-xs text-red-400">{{ errors['country'][0] }}</p>
    </div>

    <!-- Time zone -->
    <div>
      <label :for="`${idPrefix}-timezone`" class="block text-xs font-medium text-slate-400 mb-1">
        {{ t('venues.fields.timezone') }}
        <span class="text-red-400 ml-0.5">*</span>
      </label>
      <TimezoneSelect
        :id="`${idPrefix}-timezone`"
        :model-value="modelValue.timezone"
        :invalid="!!errors['timezone']"
        @update:model-value="handleTimezoneChange"
      />
      <p class="mt-1 text-xs text-slate-500">{{ t('venues.fields.timezone_hint') }}</p>
      <p v-if="timezoneSourceLine" class="mt-1 text-xs text-slate-400" data-testid="venue-timezone-source">
        {{ timezoneSourceLine }}
      </p>
      <div
        v-if="visibleSuggestion"
        class="mt-2 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2"
        role="status"
        data-testid="venue-timezone-suggestion"
      >
        <p class="text-xs text-amber-300">
          {{
            visibleSuggestion.source === 'browser'
              ? t('venues.timezone.suggestion_browser', { timezone: visibleSuggestion.timezone })
              : t('venues.timezone.suggestion', { timezone: visibleSuggestion.timezone })
          }}
        </p>
        <button
          type="button"
          class="text-xs font-semibold text-amber-300 hover:text-amber-200 underline transition-colors"
          data-testid="venue-timezone-suggestion-apply"
          @click="applySuggestion"
        >
          {{ t('venues.timezone.suggestion_apply', { timezone: visibleSuggestion.timezone }) }}
        </button>
      </div>
      <p
        v-if="changeWarning"
        class="mt-2 text-xs text-amber-400"
        role="alert"
        data-testid="venue-timezone-change-warning"
      >
        {{ changeWarning }}
      </p>
      <p v-if="errors['timezone']" class="mt-1 text-xs text-red-400">{{ errors['timezone'][0] }}</p>
    </div>
  </div>
</template>
